import React from 'react';
import moment from 'moment';
import { Tooltip } from 'antd';

import { CustomerByODService } from 'types';
import { useIntl } from 'common/config/i18n';
import { delayFormatting } from 'common/utils/date';

import FluxClientUpDisplay from './flux-client-od-up-display';
import FluxClientDownDisplay from './flux-client-od-down-display';

export function useColumns() {
  const { t } = useIntl();

  const circulationDateCol = {
    title: t('table', 'CHART_DATE'),
    key: 'circulationDate',
    width: 100,
    render: (customerService: CustomerByODService) => {
      return (
        <span style={{ color: customerService.isCancel ? 'red' : undefined }}>
          {moment(customerService.circulationDate).format('DD/MM')}
        </span>
      );
    },
  };
  const serviceNameCol = {
    title: t('table', 'CHART_TRAIN'),
    key: 'serviceName',
    width: 100,
    render: (customerService: CustomerByODService) => {
      return <span style={{ color: customerService.isCancel ? 'red' : undefined }}>{customerService.serviceName}</span>;
    },
  };
  const stopTimeCol = {
    title: t('table', 'CHART_TRAIN_STOP_DURATION'),
    key: 'stopTime',
    width: 100,
    render: (customerService: CustomerByODService) => {
      return (
        <FluxClientUpDisplay
          customerService={customerService}
          fluxClientODKey="stationStopTime"
          formatDisplay={(stopTime) => (stopTime ? (delayFormatting(stopTime.toString()) as string) : '')}
        />
      );
    },
  };

  // Departure Cols
  const departureStationCols = {
    title: t('table', 'CHART_DEPARTURE'),
    children: [
      {
        title: t('table', 'CHART_STATIONS'),
        key: 'departureShortCodeStation',
        width: 100,
        render: (customerService: CustomerByODService) => {
          return (
            <FluxClientUpDisplay
              customerService={customerService}
              fluxClientODKey="departureStationShortCode"
              leftTooltipKey="departureStationFullName"
              forceRightPlacement
            />
          );
        },
      },
      {
        title: t('table', 'CHART_TIME'),
        key: 'departureTime',
        width: 100,
        render: (customerService: CustomerByODService) => {
          return (
            <FluxClientUpDisplay
              customerService={customerService}
              fluxClientODKey="departureStationTime"
              formatDisplay={(time) => (time ? moment(time as string).format('HH:mm') : '')}
            />
          );
        },
      },
    ],
  };
  const trainNumberPassengersUpCols = {
    title: t('table', 'CHART_PASSENGER_NUMBER_UP'),
    children: [
      {
        title: (
          <Tooltip title={t('table', 'CHART_SUM_3_TOOLTIP')} placement="top">
            {t('table', 'CHART_SUM_3')}
          </Tooltip>
        ),
        key: 'allTrainNbPassengersUp',
        width: 100,
        render: (customerService: CustomerByODService) => {
          return (
            <FluxClientUpDisplay
              customerService={customerService}
              fluxClientODKey="allTrainNbPassengersUp"
              formatDisplay={(allTotal) => (allTotal ? allTotal.toString() : '0')}
            />
          );
        },
      },
      {
        title: t('table', 'CHART_SUM_1'),
        key: 'firstTrainNbPassengersUp',
        width: 100,
        render: (customerService: CustomerByODService) => {
          return (
            <FluxClientUpDisplay
              customerService={customerService}
              fluxClientODKey="firstTrainNbPassengersUp"
              formatDisplay={(firstTotal) => (firstTotal ? firstTotal.toString() : '0')}
            />
          );
        },
      },
      {
        title: t('table', 'CHART_SUM_2'),
        key: 'secondTrainNbPassengersUp',
        width: 100,
        render: (customerService: CustomerByODService) => {
          return (
            <FluxClientUpDisplay
              customerService={customerService}
              fluxClientODKey="secondTrainNbPassengersUp"
              formatDisplay={(secondTotal) => (secondTotal ? secondTotal.toString() : '0')}
            />
          );
        },
      },
    ],
  };
  const trainChildUpCol = {
    title: t('table', 'CHART_CHILDREN'),
    key: 'allTrainChildUp',
    width: 100,
    render: (customerService: CustomerByODService) => {
      return (
        <FluxClientUpDisplay
          customerService={customerService}
          fluxClientODKey="allTrainChildUp"
          formatDisplay={(child) => (child ? child.toString() : '0')}
          rightTooltipKeys={['firstTrainChildUp', 'secondTrainChildUp']}
        />
      );
    },
  };
  const trainUfrUpCol = {
    title: t('table', 'CHART_PWD'),
    key: 'allTrainUfrUp',
    width: 100,
    render: (customerService: CustomerByODService) => {
      return (
        <FluxClientUpDisplay
          customerService={customerService}
          fluxClientODKey="allTrainUfrUp"
          formatDisplay={(ufr) => (ufr ? ufr.toString() : '0')}
          rightTooltipKeys={['firstTrainUfrUp', 'secondTrainUfrUp']}
        />
      );
    },
  };

  // Arrival Cols
  const arrivalStationCols = {
    title: t('table', 'CHART_ARRIVAL'),
    children: [
      {
        title: t('table', 'CHART_STATIONS'),
        key: 'arrivalShortCodeStation',
        width: 100,
        render: (customerService: CustomerByODService) => {
          return (
            <FluxClientDownDisplay
              customerService={customerService}
              fluxClientODKey="arrivalStationShortCode"
              leftTooltipKey="arrivalStationFullName"
              rightTooltipKeys={['allDepartureNbPassengersDown']}
            />
          );
        },
      },
      {
        title: t('table', 'CHART_TIME'),
        key: 'arrivalTime',
        width: 100,
        render: (customerService: CustomerByODService) => {
          return (
            <FluxClientDownDisplay
              customerService={customerService}
              fluxClientODKey="arrivalStationTime"
              formatDisplay={(time) => (time ? moment(time as string).format('HH:mm') : '')}
            />
          );
        },
      },
    ],
  };
  const trainNumberPassengersDownCols = {
    title: t('table', 'CHART_PASSENGER_NUMBER_DOWN'),
    children: [
      {
        title: (
          <Tooltip title={t('table', 'CHART_SUM_3_TOOLTIP')} placement="top">
            {t('table', 'CHART_SUM_3')}
          </Tooltip>
        ),
        key: 'allTrainNbPassengersDown',
        width: 100,
        render: (customerService: CustomerByODService) => {
          return (
            <FluxClientDownDisplay
              customerService={customerService}
              fluxClientODKey="allTrainNbPassengersDown"
              formatDisplay={(allTotal) => (allTotal ? allTotal.toString() : '0')}
            />
          );
        },
      },
      {
        title: t('table', 'CHART_SUM_1'),
        key: 'firstTrainNbPassengersDown',
        width: 100,
        render: (customerService: CustomerByODService) => {
          return (
            <FluxClientDownDisplay
              customerService={customerService}
              fluxClientODKey="firstTrainNbPassengersDown"
              formatDisplay={(firstTotal) => (firstTotal ? firstTotal.toString() : '0')}
            />
          );
        },
      },
      {
        title: t('table', 'CHART_SUM_2'),
        key: 'secondTrainNbPassengersDown',
        width: 100,
        render: (customerService: CustomerByODService) => {
          return (
            <FluxClientDownDisplay
              customerService={customerService}
              fluxClientODKey="secondTrainNbPassengersDown"
              formatDisplay={(secondTotal) => (secondTotal ? secondTotal.toString() : '0')}
            />
          );
        },
      },
    ],
  };
  const trainChildDownCol = {
    title: t('table', 'CHART_CHILDREN'),
    key: 'allTrainChildDown',
    width: 100,
    render: (customerService: CustomerByODService) => {
      return (
        <FluxClientDownDisplay
          customerService={customerService}
          fluxClientODKey="allTrainChildDown"
          formatDisplay={(child) => (child ? child.toString() : '0')}
          rightTooltipKeys={['firstTrainChildDown', 'secondTrainChildDown']}
        />
      );
    },
  };
  const trainUfrDownCol = {
    title: t('table', 'CHART_PWD'),
    key: 'allTrainUfrDown',
    width: 100,
    render: (customerService: CustomerByODService) => {
      return (
        <FluxClientDownDisplay
          customerService={customerService}
          fluxClientODKey="allTrainUfrDown"
          formatDisplay={(ufr) => (ufr ? ufr.toString() : '0')}
          rightTooltipKeys={['firstTrainUfrDown', 'secondTrainUfrDown']}
        />
      );
    },
  };

  const Columns_FR = [
    { ...circulationDateCol },
    { ...serviceNameCol },
    { ...stopTimeCol },

    // Departure Cols
    { ...departureStationCols },
    { ...trainNumberPassengersUpCols },
    { ...trainChildUpCol },
    { ...trainUfrUpCol },

    // Arrival Cols
    { ...arrivalStationCols },
    { ...trainNumberPassengersDownCols },
    { ...trainChildDownCol },
    { ...trainUfrDownCol },
  ];

  const Columns_ES = [
    { ...circulationDateCol },
    { ...serviceNameCol },
    { ...stopTimeCol },

    // Departure Cols
    { ...departureStationCols },
    { ...trainNumberPassengersUpCols },
    {
      title: (
        <>
          <div>{t('table', 'HART_PWD1')}</div>
          <div>{t('table', 'HART_PWD0')}</div>
        </>
      ),
      key: 'allTrainAssistanceUp',
      width: 250,
      render: (customerService: CustomerByODService) => {
        return (
          <FluxClientUpDisplay
            customerService={customerService}
            fluxClientODKey="allTrainAssistanceUp"
            formatDisplay={(assistance) => (assistance ? assistance.toString() : '0')}
            rightTooltipKeys={['firstTrainAssistanceUp', 'secondTrainAssistanceUp']}
          />
        );
      },
    },
    { ...trainUfrUpCol },
    { ...trainChildUpCol },
    {
      title: t('table', 'CHART_BAMB'),
      key: 'allTrainBabyUp',
      width: 100,
      render: (customerService: CustomerByODService) => {
        return (
          <FluxClientUpDisplay
            customerService={customerService}
            fluxClientODKey="allTrainBabyUp"
            formatDisplay={(baby) => (baby ? baby.toString() : '0')}
          />
        );
      },
    },

    // Arrival Cols
    { ...arrivalStationCols },
    { ...trainNumberPassengersDownCols },
    {
      title: (
        <>
          <div>{t('table', 'HART_PWD1')}</div>
          <div>{t('table', 'HART_PWD0')}</div>
        </>
      ),
      key: 'allTrainAssistanceDown',
      width: 250,
      render: (customerService: CustomerByODService) => {
        return (
          <FluxClientDownDisplay
            customerService={customerService}
            fluxClientODKey="allTrainAssistanceDown"
            formatDisplay={(assistance) => (assistance ? assistance.toString() : '0')}
            rightTooltipKeys={['firstTrainAssistanceDown', 'secondTrainAssistanceDown']}
          />
        );
      },
    },
    { ...trainUfrDownCol },
    { ...trainChildDownCol },
    {
      title: t('table', 'CHART_BAMB'),
      key: 'allTrainBabyDown',
      width: 100,
      render: (customerService: CustomerByODService) => {
        return (
          <FluxClientDownDisplay
            customerService={customerService}
            fluxClientODKey="allTrainBabyDown"
            formatDisplay={(baby) => (baby ? baby.toString() : '0')}
          />
        );
      },
    },
  ];

  return {
    FR: Columns_FR,
    ES: Columns_ES,
  };
}
